<template>
  <div class="row">
    <div class="col-md-12">
      <h1 class="h3 mb-3 text-gray-800">Gestão e Acompanhamento de Vagas</h1>
    </div>

    <load
      mode="table"
      v-if="loading"
      message="Carregando solicitações de vagas"
    />

    <div class="col-md-12" v-else>
      <div class="card mb-0 shadow">
        <div class="card-body">
          <div class="tab-content animate__animated animate__fadeIn">
            <!-- Filtros -->
            <div class="row mb-4">
              <form @submit.prevent="getItens" class="col-12">
                <div class="row">
                  <form-select
                    v-model="filtros.publicoAlvo"
                    label="Público-alvo"
                    grid="4"
                    :itens="$root.arrays.acolhimento_publico_alvo"
                    :multiple="true"
                  />

                  <form-input
                    v-model="filtros.nome"
                    label="Buscar usuário (nome ou nome social)"
                    grid="4"
                  />

                  <form-select
                    v-model="filtros.status"
                    label="Status"
                    grid="4"
                    :itens="statusOptions"
                    :multiple="true"
                  />

                  <form-select
                    v-model="filtros.unidadeOrigem"
                    label="Unidade de Origem"
                    grid="4"
                    :itens="unidades"
                    :multiple="true"
                    :loading="loadingUnidades"
                  />

                  <form-calendar
                    v-model="filtros.dataSolicitacao"
                    label="Data da Solicitação pela Unidade de Origem"
                    grid="4"
                    range
                  />
                </div>

                <div class="row pt-4 mb-4 d-flex justify-content-end">
                  <div class="col-auto px-2">
                    <button
                      type="button"
                      class="btn btn-secondary"
                      @click="limparFiltros"
                    >
                      Limpar Filtros
                    </button>
                    <button
                      class="btn btn-success"
                      type="submit"
                    >
                      <i class="fas fa-search"></i>
                      Buscar
                    </button>
                  </div>
                </div>
              </form>
            </div>

            <!-- Tabela -->
            <b-table
              :fields="fields"
              :items="itemList"
              :responsive="true"
              striped
              hover
              show-empty
              small
              outlined
              stacked="xs"
              table-class="mb-3"
            >
              <template v-slot:cell(detalhes)="data">
               <div class="small">
                <div><b>Usuário:</b> {{ data.item.nome_usuario }}</div>
                <div><b>Idade:</b> {{ calcularIdade(data.item.nascimento) }}</div>
                <div><b>Origem da Solicitação:</b> {{ formatarOrigemSolicitacao(data.item.origem_solicitacao_id) }}</div>
                <div><b>Data da solicitação pela unidade de origem:</b> {{ formatarData(data.item.data_solicitacao) }}</div>
                <div><b>Público:</b> {{ getNomePublicoAlvo(data.item.publico_alvo_id) }}</div>
                <div v-if="data.item.situacao_rua"><b>Pessoa em situação de rua</b></div>
                <div v-if="data.item.deficiencia"><b>Pessoa com deficiência: {{ data.item.tipo_deficiencia }}</b></div>  
                <div><b>Técnico:</b> {{ data.item.nome_responsavel }}</div>
                <div><b>Unidade:</b> {{ data.item.unidade_solicitante }}</div>
                <div v-if="data.item.status_solicitacao == 'ACOLHIDO'"> <b>Unidade do acolhimento: {{ data.item.nome_unidade }}</b> </div>  
               </div>
              </template>

              <template v-slot:cell(data_solicitacao)="data">
                <span v-if="data.item.status_solicitacao === 'ACOLHIDO'">
                  {{ formatarData(data.item.updated_at) }}
                </span>
                <span v-else>
                  {{ formatarData(data.item.created_at) }}
                </span>
              </template>

              <template v-slot:cell(status)="data">
                <span v-if="data.item.status_solicitacao === 'Pendente'" class="text-warning font-weight-bold">
                  AGUARDANDO VAGA
                </span>
                <span v-else-if="data.item.status_solicitacao === 'AUTORIZADO'" class="text-danger font-weight-bold">
                  AUTORIZADO PARA CADASTRAR ACOLHIMENTO
                </span>
                <span v-else-if="data.item.status_solicitacao === 'ACOLHIDO'" class="text-success font-weight-bold">
                  ACOLHIMENTO REALIZADO
                </span>
                <span v-else>
                  EXCLUÍDO
                </span>
              </template>

              <template v-slot:cell(actions)="data">
                <div class="d-flex justify-content-start flex-wrap">
                  <!-- Se o status for DELETADO, mostrar só o botão visualizar/imprimir -->
                  <template v-if="data.item.status_solicitacao === 'DELETADO' || data.item.status_solicitacao === 'ACOLHIDO'">
                    <!-- Só Visualizar -->
                    <button
                      class="btn btn-primary btn-xs mx-1"
                      title="Visualizar/Imprimir"
                      @click="abrirVisualizarImprimir(data.item.id)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>
                  </template>

                  <template v-else-if="data.item.status_solicitacao === 'AUTORIZADO'">
                    <!-- Visualizar + Cadastrar Acolhimento -->
                    <button
                      class="btn btn-primary btn-xs mx-1"
                      title="Visualizar/Imprimir"
                      @click="abrirVisualizarImprimir(data.item.id)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>

                    <button
                      v-if="isGestor"
                      class="btn btn-success btn-xs mx-1"
                      title="Cadastrar Acolhimento"
                      @click="abrirCadastroAcolhimento(data.item)"
                    >
                      <i class="fas fa-plus"></i>
                    </button>
                  </template>

                  <template v-else>
                    <!-- Todos os botões -->
                    <button
                      class="btn btn-primary btn-xs mx-1"
                      title="Visualizar/Imprimir"
                      @click="abrirVisualizarImprimir(data.item.id)"
                    >
                      <i class="fas fa-eye"></i>
                    </button>

                    <button
                      v-if="isGestor"
                      class="btn btn-success btn-xs mx-1"
                      title="Editar"
                      @click="abrirEdicao(data.item)"
                    >
                      <i class="fas fa-edit"></i>
                    </button>

                    <button
                      v-if="isGestor"
                      class="btn btn-info btn-xs mx-1"
                      title="Autorizar Acolhimento"
                      @click="abrirAutorizacaoVaga(data.item)"
                    >
                      <i class="fas fa-check-circle"></i>
                    </button>

                    <button
                      v-if="isGestor"
                      class="btn btn-danger btn-xs mx-1"
                      title="Excluir"
                      @click="abrirExclusaoVaga(data.item)"
                    >
                      <i class="fas fa-trash-alt"></i>
                    </button>
                  </template>
                </div>
              </template>

              <template v-slot:empty>
                <div class="text-center my-5">
                  <img src="/img/icons/robot.svg" alt="Sem resultados" class="mb-4" style="max-height: 150px;" />
                  <h4>Nenhuma solicitação encontrada</h4>
                </div>
              </template>
            </b-table>
            <div class="row align-items-center">
                <div class="col-lg-6 col-md-12">
                    <div class="form-group d-inline-flex mb-0">
                    <select
                        class="form-control"
                        style="width: 70px"
                        v-model="perPage"
                        @change="getItens"
                    >
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="25">25</option>
                        <option value="50">50</option>
                    </select>
                    </div>
                    <div class="d-inline-flex ml-2">
                    <b-pagination
                        :per-page="perPage"
                        :total-rows="pagination.total"
                        v-model="page"
                        @change="getItens"
                    />
                    </div>
                </div>
                <div class="col-lg-6 col-md-12 mt-3 mt-lg-0 text-right">
                    Exibindo página {{ pagination.currentPage }} de
                    {{ pagination.totalPages }} ({{ pagination.total }} registros)
                </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <ModalExclusaoVaga v-if="vagaSelecionada" :item="vagaSelecionada"/>
    <ModalSolicitarVaga ref="modalSolicitarVaga" />
    <ModalAutorizacaoVaga v-if="vagaSelecionada" :item="vagaSelecionada" :unidades="unidades" :todas_unidades="todas_unidades" :loadingUnidades="loadingUnidades"/>

  </div>
</template>

<script>

import ModalExclusaoVaga from "./includes/ExclusaoVaga";
import ModalSolicitarVaga from "./includes/ModalSolicitarVaga";
import ModalAutorizacaoVaga from "./includes/AutorizacaoVaga";

export default {
  components: {ModalExclusaoVaga, ModalSolicitarVaga, ModalAutorizacaoVaga},
  name: "GestaoVagas",
  data() {
    return {
        vagaSelecionada: null,
        loading: true,
        unidades: [],
        todas_unidades: [],
        loadingUnidades: false,
        filtros: {
          publicoAlvo: [],
          unidadeOrigem: [],
          dataSolicitacao: null,
          status: [],
          nome: ""
        },
        itemList: [],
        perPage: 10,
        page: 1,
        pagination: {
            total: 0,
            perPage: 10,
            currentPage: 1,
            totalPages: 1,
        },
        perPage: 10,
        page: 1,
        fields: [
            { key: "detalhes", label: "Detalhes" },
            { key: "data_solicitacao", label: "Data da Solicitação" },
            { key: "status", label: "Status" },
            {
                key: "actions",
                label: "",
                class: "text-right no-print",
                thStyle: { width: "95px", minWidth: "95px" },
            },
        ],
        statusOptions: [
          { id: 'Pendente', value: 'AGUARDANDO VAGA' },
          { id: 'AUTORIZADO', value: 'AUTORIZADO PARA CADASTRAR ACOLHIMENTO' },
          { id: 'ACOLHIDO', value: 'ACOLHIMENTO REALIZADO' },
          { id: 'DELETADO', value: 'EXCLUÍDO' }
        ],
    };
  },
  mounted() {
    this.getUnidades();
    this.getItens();
    this.getTodasUnidades();

    if (this.$store.state.user) {
      let role_user = this.$store.state.user.role_details.name;
      this.isGestor = role_user === "Gestor(a)" || role_user === "Admin" || role_user === "Coordenador" || role_user === "Técnico(a) de Nível Superior Gestão";
    }
  },
  methods: {
    formatarOrigemSolicitacao(id) {
      const lista = this.$root.arrays.acolhimento_origem_solicitacao || [];
      const item = lista.find((el) => el.id == id);
      return item ? item.value : "-";
    },
    abrirAutorizacaoVaga(item){
      this.vagaSelecionada = item;
      this.$bvModal.show('autorizacao-acolhimento');
    },
    abrirExclusaoVaga(item) {
      this.vagaSelecionada = item;
      this.$bvModal.show('exclusao-solicitacao-vaga');
    },
    abrirEdicao(item) {
      this.$root.$emit('abrirModalVaga', { modo: 'editar', item });
    },
    getNomePublicoAlvo(id) {
      if (!id) return '-';
        const item = this.$root.arrays.acolhimento_publico_alvo.find(el => el.id === id);
        return item ? item.value : '-';
    },
    formatarData(dataStr) {
      if (!dataStr) return '-';
        const data = new Date(dataStr);
      if (isNaN(data)) return '-';
        const dia = String(data.getDate()).padStart(2, '0');
        const mes = String(data.getMonth() + 1).padStart(2, '0');
        const ano = data.getFullYear();
      return `${dia}/${mes}/${ano}`;
    },
    calcularIdade(nascimento) {
      if (!nascimento) return 'Não informado';
        const nascimentoDate = new Date(nascimento);
        const diff = Date.now() - nascimentoDate.getTime();
        const idade = new Date(diff).getUTCFullYear() - 1970;
      return idade > 0 ? idade : '- anos';
    },
    abrirVisualizarImprimir(id) {
      this.$router.push(`/dashboard/acolhimento/gestao-vagas/${id}`);
    },
    abrirCadastroAcolhimento(item) {
      window.__itemParaCadastroAcolhimento = item; // variável global temporária
      this.$router.push(`/dashboard/acolhimento/add-acolhimento`);
    },
    getUnidades() { 
      this.load = true;
      axios.get("api/dashboard/acolhimentos/unidades").then((response) => {
        this.unidades = response.data.data
          .filter((item) => item.unidade)
          .map((item) => ({
            id: item.unidade.id,
            value: item.unidade.nome,
            publico_alvo: item.publico_alvo || []
          }));
      });
    },
    getTodasUnidades() { 
      this.load = true;
      axios.get("api/dashboard/unidades").then((response) => {
          this.todas_unidades = response.data.data.map((item) => ({
              id: String(item.id),
              value: item.nome
          }));
      });
    },
    getItens() {
      this.loading = true;

      const statusArray = this.filtros.status || [];

      const params = {
        publico_alvo_id: this.filtros.publicoAlvo,
        unidade_origem_id: this.filtros.unidadeOrigem,
        data_inicio: this.filtros.dataSolicitacao?.start,
        data_fim: this.filtros.dataSolicitacao?.end,
        perPage: this.perPage,
        page: this.page,
        nome: this.filtros.nome,
      };

      if (statusArray.length === 0) {
        params.excluir_status = 'DELETADO';
      } else {
        params.status = statusArray.join(',');
      }

      axios
        .get("api/dashboard/acolhimento/solicitacao/buscar", { params })
        .then((res) => {
          this.itemList = res.data.data;
          this.pagination = res.data.pagination || {
            total: 0,
            perPage: this.perPage,
            currentPage: this.page,
            totalPages: 1,
          };
          this.loading = false;
        })
        .catch(() => (this.loading = false));
    },
    limparFiltros() {
      this.filtros = {
            publicoAlvo: [],
            unidadeOrigem: [],
            dataSolicitacao: null,
            status: [],
            nome: ""
        };
        this.page = 1;
        this.getItens();
    },
  },
};
</script>